import type { DegreeSectionStatus, IDocumentsDegree, IUniversityDegree } from '../../types/newStudent/documents.interface';

/** Fallback para cálculo legado quando a API não envia `*SectionStatus`. */
export const META_HORAS_ESTAGIO_COMPLEMENTAR = 200;

/** Normaliza situação da API (PT/EN), alinhado ao normalizeSituacaoProfessor do Laravel. */
export function normalizeDegreeSituation(s: string | undefined | null): string {
  const t = (s ?? '').trim().toLowerCase();
  if (t === 'rejected' || t === 'reprovado') return 'reprovado';
  if (t === 'approved' || t === 'aprovado') return 'aprovado';
  if (t === 'concluído' || t === 'concluido') return 'aprovado';
  if (t === 'waiting' || t === 'aguardando') return 'aguardando';
  return t;
}

/** Linha conta para a soma de horas aprovadas (mesma ideia do meta no Laravel). */
export function lineCountsApprovedWorkload(item: IDocumentsDegree): boolean {
  const n = normalizeDegreeSituation(item.situation);
  if (n === 'reprovado') return false;
  if (n === 'aprovado') return true;
  if (n === 'aguardando' || n === '') {
    return Boolean(item.checked);
  }
  return false;
}

export function isApprovedDegreeSituation(s: string | undefined | null): boolean {
  return normalizeDegreeSituation(s) === 'aprovado';
}

export function sumHoursDegree(items: IDocumentsDegree[]) {
  const horasAguardando = items
    .filter(
      (i) =>
        normalizeDegreeSituation(i.situation) !== 'reprovado' && !lineCountsApprovedWorkload(i),
    )
    .reduce((acc, i) => acc + Number(i.workload ?? 0), 0);
  const horasAprovadas = items
    .filter(lineCountsApprovedWorkload)
    .reduce((acc, i) => acc + Number(i.workload ?? 0), 0);
  return { horasAguardando, horasAprovadas };
}

/** Uso legado / testes; a UI deve preferir `internshipSectionStatus` da API. */
export function isInternshipMetaHorasComplete(
  items: IDocumentsDegree[],
  metaHoras: number = META_HORAS_ESTAGIO_COMPLEMENTAR,
): boolean {
  return sumHoursDegree(items).horasAprovadas >= metaHoras;
}

/** Uso legado / testes; a UI deve preferir `complementarySectionStatus` da API. */
export function isComplementaryMetaHorasComplete(
  items: IDocumentsDegree[],
  metaHoras: number = META_HORAS_ESTAGIO_COMPLEMENTAR,
): boolean {
  return sumHoursDegree(items).horasAprovadas >= metaHoras;
}

/** Preferência: `data.internshipSectionStatus` (backend). Fallback só para respostas antigas. */
export function resolveInternshipSectionStatus(data: IUniversityDegree): DegreeSectionStatus {
  if (data.internshipSectionStatus) {
    return data.internshipSectionStatus;
  }
  if (data.internship.length === 0) return 'pendente';
  if (isInternshipMetaHorasComplete(data.internship)) return 'concluido';
  return 'andamento';
}

/** Preferência: `data.complementarySectionStatus` (backend). Fallback só para respostas antigas. */
export function resolveComplementarySectionStatus(data: IUniversityDegree): DegreeSectionStatus {
  if (data.complementarySectionStatus) {
    return data.complementarySectionStatus;
  }
  if (data.complementaryActivity.length === 0) return 'pendente';
  if (isComplementaryMetaHorasComplete(data.complementaryActivity)) return 'concluido';
  return 'andamento';
}

/** @deprecated Prefer isInternshipMetaHorasComplete e isComplementaryMetaHorasComplete (meta por seção). */
export function isEstagioComplementarMetaHorasAtingida(
  internship: IDocumentsDegree[],
  complementary: IDocumentsDegree[],
): boolean {
  const a =
    sumHoursDegree(internship).horasAprovadas + sumHoursDegree(complementary).horasAprovadas;
  return a >= META_HORAS_ESTAGIO_COMPLEMENTAR;
}

/**
 * Ajusta o rótulo por linha: "Concluído" só quando a meta de horas da própria seção foi atingida.
 * Evita rótulo defasado da API após reprovação ou redução de CH.
 */
export function normalizeDegreeItemsForPortfolio(
  items: IDocumentsDegree[],
  portfolioMetaOk: boolean,
): IDocumentsDegree[] {
  return items.map((item) => {
    if (normalizeDegreeSituation(item.situation) === 'reprovado') {
      return { ...item, situation: 'reprovado' };
    }
    if (!lineCountsApprovedWorkload(item)) {
      return { ...item, situation: 'aguardando' };
    }
    return {
      ...item,
      situation: portfolioMetaOk ? 'Concluído' : 'aprovado',
    };
  });
}

/** Linha “nova” ainda não enviada (sem URL no servidor). */
export function isDraftDocumentsDegreeRow(item: IDocumentsDegree): boolean {
  return !item.url && (item.id === '' || item.id === undefined || item.id === null);
}
